import { drawMarkShape, markShapeOf } from "@/lib/annotation-mark-shape";
import {
  medicationNumberFontSize,
  medicationNumberOutline,
} from "@/lib/annotation-number";
import {
  pinAnchorRadius,
  pinBadgeCornerRadius,
  pinBadgeFontSize,
  pinBadgeSize,
  pinBadgeTextColor,
  pinLabelText,
  pinLeaderEnd,
  pinScaleForImage,
  roundedRectPath,
} from "@/lib/annotation-pin";
import { dashPattern } from "@/lib/annotation-stroke";
import { formatDate, formatUnit } from "@/lib/format-data";

import {
  Annotation,
  DotAnnotation,
  MedicationAnnotation,
  PinAnnotation,
  SyringeLineAnnotation,
} from "@/types/annotation";
import { Unit } from "@/types/enums/unit";

const LEGEND_TEXT_COLOR = "#1f2937"; // matches CANVAS_TEXT_COLOR in image-annotator

/**
 * Bake annotations onto an image, returning a PNG blob at the image's
 * natural dimensions. Mirrors the SVG overlay shapes in
 * `scribe/playground/[id]/annotation-overlay.tsx` so exports match what a
 * provider sees in the gallery, but without the thumbnail crop/scale.
 */
export async function renderAnnotatedImage(
  imageBlob: Blob,
  annotations: Annotation[] | null | undefined,
): Promise<Blob> {
  const bitmap = await createImageBitmap(imageBlob);
  const width = bitmap.width;
  const height = bitmap.height;

  const canvas =
    typeof OffscreenCanvas !== "undefined"
      ? new OffscreenCanvas(width, height)
      : Object.assign(document.createElement("canvas"), { width, height });

  // OffscreenCanvas and HTMLCanvasElement both expose a 2D context with the
  // same drawing API, but their TypeScript types only intersect via a cast.
  // Always release the decoded bitmap, even if context acquisition or
  // drawing throws — leaking it across attachments would accumulate in
  // browser memory during large exports.
  let ctx: CanvasRenderingContext2D;
  try {
    const acquired = (
      canvas as { getContext(type: "2d"): CanvasRenderingContext2D | null }
    ).getContext("2d");
    if (!acquired) throw new Error("2D canvas context unavailable");
    ctx = acquired;
    ctx.drawImage(bitmap, 0, 0, width, height);
  } finally {
    bitmap.close?.();
  }

  if (annotations && annotations.length > 0) {
    drawAnnotations(ctx, annotations, width, height);
    const legend = computeMedicationLegend(annotations);
    if (legend.length > 0) {
      drawMedicationLegend(ctx, legend, width, height);
    }
  }

  if (
    typeof OffscreenCanvas !== "undefined" &&
    canvas instanceof OffscreenCanvas
  ) {
    return await canvas.convertToBlob({ type: "image/png" });
  }
  return await new Promise<Blob>((resolve, reject) => {
    (canvas as HTMLCanvasElement).toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error("Failed to encode canvas")),
      "image/png",
    );
  });
}

function drawAnnotations(
  ctx: CanvasRenderingContext2D,
  annotations: Annotation[],
  width: number,
  height: number,
) {
  // The overlay uses a stroke multiplier of 0.8 inside a 200px thumbnail. At
  // full natural resolution we scale strokes up so they remain visually
  // proportional to what the gallery shows.
  const minDim = Math.min(width, height);
  const strokeScale = minDim / 200;

  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  for (const annotation of annotations) {
    ctx.strokeStyle = annotation.color;
    ctx.fillStyle = annotation.color;
    ctx.setLineDash([]);

    switch (annotation.type) {
      case "pen":
      case "syringeLine": {
        const extra = annotation.type === "syringeLine" ? 1 : 0;
        ctx.lineWidth = (annotation.strokeWidth + extra) * 0.8 * strokeScale;
        ctx.setLineDash(dashPattern(annotation.strokeStyle, ctx.lineWidth));
        ctx.beginPath();
        annotation.points.forEach((p, i) => {
          const x = p.x * width;
          const y = p.y * height;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
        ctx.stroke();
        break;
      }

      case "arrow": {
        ctx.lineWidth = annotation.strokeWidth * 0.8 * strokeScale;
        ctx.setLineDash(dashPattern(annotation.strokeStyle, ctx.lineWidth));
        const sx = annotation.start.x * width;
        const sy = annotation.start.y * height;
        const ex = annotation.end.x * width;
        const ey = annotation.end.y * height;
        drawArrow(ctx, sx, sy, ex, ey, 8 * strokeScale);
        break;
      }

      case "circle": {
        ctx.lineWidth = annotation.strokeWidth * 0.8 * strokeScale;
        ctx.setLineDash(dashPattern(annotation.strokeStyle, ctx.lineWidth));
        const cx = annotation.center.x * width;
        const cy = annotation.center.y * height;
        const r = annotation.radius * minDim;
        drawMarkShape(ctx, markShapeOf(annotation), cx, cy, r);
        break;
      }

      case "dot": {
        const x = annotation.position.x * width;
        const y = annotation.position.y * height;
        const r = annotation.radius
          ? annotation.radius * minDim
          : 2 * strokeScale;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.lineWidth = 0.5 * strokeScale;
        ctx.strokeStyle = "white";
        ctx.stroke();
        ctx.strokeStyle = annotation.color;
        break;
      }

      case "pin": {
        const siteX = annotation.position.x * width;
        const siteY = annotation.position.y * height;
        const badgeX = annotation.label.x * width;
        const badgeY = annotation.label.y * height;
        const labelText = pinLabelText(annotation);
        // Pin geometry scales off the image, not off strokeScale's 200px
        // thumbnail baseline, which made badges enormous at full resolution.
        const pinScale = pinScaleForImage(minDim);
        const badge = pinBadgeSize(labelText, pinScale);
        const leaderEnd = pinLeaderEnd(
          { x: siteX, y: siteY },
          { x: badgeX, y: badgeY },
          badge,
        );

        ctx.lineWidth = Math.max(annotation.strokeWidth * 0.5, 1) * strokeScale;
        ctx.setLineDash(dashPattern(annotation.strokeStyle, ctx.lineWidth));
        ctx.beginPath();
        ctx.moveTo(siteX, siteY);
        ctx.lineTo(leaderEnd.x, leaderEnd.y);
        ctx.stroke();
        ctx.setLineDash([]);

        ctx.beginPath();
        ctx.arc(siteX, siteY, pinAnchorRadius(pinScale), 0, Math.PI * 2);
        ctx.fill();
        ctx.lineWidth = 0.5 * strokeScale;
        ctx.strokeStyle = "white";
        ctx.stroke();
        ctx.strokeStyle = annotation.color;

        ctx.beginPath();
        roundedRectPath(
          ctx,
          badgeX - badge.width / 2,
          badgeY - badge.height / 2,
          badge.width,
          badge.height,
          pinBadgeCornerRadius(pinScale),
        );
        ctx.fill();

        ctx.fillStyle = pinBadgeTextColor(annotation.color);
        ctx.font = `bold ${pinBadgeFontSize(pinScale)}px sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(labelText, badgeX, badgeY);
        ctx.textAlign = "left";
        ctx.textBaseline = "alphabetic";
        ctx.fillStyle = annotation.color;
        break;
      }

      case "comment": {
        const x = annotation.position.x * width;
        const y = annotation.position.y * height;
        // Match the simple truncation/wrapping from the SVG thumbnail
        // overlay; this is intentionally not feature-complete with the rich
        // editor, just legible in the exported PNG.
        const text =
          annotation.text.length > 20
            ? annotation.text.substring(0, 20) + "..."
            : annotation.text;
        const lines: string[] = [];
        for (let i = 0; i < text.length; i += 12) {
          lines.push(text.substring(i, i + 12));
        }
        const fontSize = 7 * strokeScale;
        const lineHeight = 10 * strokeScale;
        const boxWidth = 60 * strokeScale;
        const boxHeight = lines.length * lineHeight + 6 * strokeScale;

        ctx.save();
        ctx.setLineDash([2 * strokeScale, 1.5 * strokeScale]);
        ctx.lineWidth = 0.5 * strokeScale;
        ctx.beginPath();
        roundedRect(ctx, x, y, boxWidth, boxHeight, 2 * strokeScale);
        ctx.stroke();
        ctx.restore();

        ctx.font = `${fontSize}px sans-serif`;
        ctx.textBaseline = "alphabetic";
        lines.forEach((line, i) => {
          ctx.fillText(
            line,
            x + 3 * strokeScale,
            y + 8 * strokeScale + i * lineHeight,
          );
        });
        break;
      }

      case "medication": {
        const baseFontSize = medicationNumberFontSize(
          annotation.strokeWidth,
          pinScaleForImage(minDim),
        );
        ctx.font = `bold ${baseFontSize}px sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        if (
          annotation.shape === "arrow" &&
          annotation.start &&
          annotation.end
        ) {
          ctx.lineWidth = Math.max(
            annotation.strokeWidth * 0.8 * strokeScale,
            0.5 * strokeScale,
          );
          const sx = annotation.start.x * width;
          const sy = annotation.start.y * height;
          const ex = annotation.end.x * width;
          const ey = annotation.end.y * height;
          const arrowLen =
            (3 + (annotation.strokeWidth || 1) * 1.5) * strokeScale;
          drawArrow(ctx, sx, sy, ex, ey, arrowLen, true);
          drawOutlinedText(
            ctx,
            String(annotation.amount),
            sx,
            sy,
            medicationNumberOutline(baseFontSize),
            1,
            annotation.color,
          );
        } else if (annotation.shape === "circle" && annotation.position) {
          const x = annotation.position.x * width;
          const y = annotation.position.y * height;
          drawOutlinedText(
            ctx,
            String(annotation.amount),
            x,
            y,
            medicationNumberOutline(baseFontSize),
            1,
            annotation.color,
          );
        }
        ctx.textAlign = "start";
        ctx.textBaseline = "alphabetic";
        break;
      }
    }
  }
}

function drawArrow(
  ctx: CanvasRenderingContext2D,
  sx: number,
  sy: number,
  ex: number,
  ey: number,
  headLen: number,
  filledHead = false,
) {
  ctx.beginPath();
  ctx.moveTo(sx, sy);
  ctx.lineTo(ex, ey);
  ctx.stroke();

  // The head stays solid even on a dashed arrow — a dashed head reads as noise.
  ctx.setLineDash([]);
  const angle = Math.atan2(ey - sy, ex - sx);
  const armAngle = Math.PI / 6;
  const h1x = ex - headLen * Math.cos(angle - armAngle);
  const h1y = ey - headLen * Math.sin(angle - armAngle);
  const h2x = ex - headLen * Math.cos(angle + armAngle);
  const h2y = ey - headLen * Math.sin(angle + armAngle);

  ctx.beginPath();
  ctx.moveTo(h1x, h1y);
  ctx.lineTo(ex, ey);
  ctx.lineTo(h2x, h2y);
  if (filledHead) {
    ctx.closePath();
    ctx.fill();
  } else {
    ctx.stroke();
  }
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
}

interface MedicationLegendEntry {
  itemName: string;
  unit: Unit;
  color: string;
  date: string;
  totalAmount: number;
  count: number;
}

// Mirrors the in-app medication legend computed in image-annotator.tsx
// (groups by itemId + color + local date; medication and pin amounts are
// summed, syringeLine/dot amounts take the max because one prescription can be
// distributed across multiple sites).
function computeMedicationLegend(
  annotations: Annotation[],
): MedicationLegendEntry[] {
  type MedAnnotation = (
    | MedicationAnnotation
    | SyringeLineAnnotation
    | DotAnnotation
    | PinAnnotation
  ) & {
    itemId: string;
    itemName: string;
    amount: number;
    unit: Unit;
  };

  const medications = annotations.filter((a): a is MedAnnotation => {
    if (a.type === "medication" || a.type === "syringeLine") return true;
    if (a.type !== "dot" && a.type !== "pin") return false;
    // Dot and pin annotations carry medication tracking only when all the
    // legend fields are populated; without them the aggregation would produce
    // NaN totals and "undefined" labels in the rendered legend.
    const marker = a as DotAnnotation | PinAnnotation;
    return (
      !!marker.itemId &&
      !!marker.itemName &&
      !!marker.unit &&
      typeof marker.amount === "number" &&
      Number.isFinite(marker.amount)
    );
  });

  const grouped = medications.reduce(
    (acc, med) => {
      let dateStr = "";
      if (med.createdAt) {
        const d = new Date(med.createdAt);
        if (!isNaN(d.getTime())) {
          const yyyy = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, "0");
          const dd = String(d.getDate()).padStart(2, "0");
          dateStr = `${yyyy}-${mm}-${dd}`;
        }
      }
      const key = `${med.itemId}-${med.color}-${dateStr}`;
      if (!acc[key]) {
        acc[key] = {
          itemName: med.itemName,
          unit: med.unit,
          color: med.color,
          date: dateStr,
          medicationSum: 0,
          syringeLineSum: 0,
          count: 0,
        };
      }
      if (med.type === "medication" || med.type === "pin") {
        acc[key].medicationSum += med.amount;
      } else {
        acc[key].syringeLineSum = Math.max(acc[key].syringeLineSum, med.amount);
      }
      acc[key].count += 1;
      return acc;
    },
    {} as Record<
      string,
      {
        itemName: string;
        unit: Unit;
        color: string;
        date: string;
        medicationSum: number;
        syringeLineSum: number;
        count: number;
      }
    >,
  );

  return Object.values(grouped).map((item) => ({
    itemName: item.itemName,
    unit: item.unit,
    color: item.color,
    date: item.date,
    // Floating point cleanup (e.g. 8.000000000000004 -> 8) — same as the
    // in-app legend.
    totalAmount:
      Math.round((item.medicationSum + item.syringeLineSum) * 10000) / 10000,
    count: item.count,
  }));
}

function drawMedicationLegend(
  ctx: CanvasRenderingContext2D,
  legend: MedicationLegendEntry[],
  width: number,
  height: number,
) {
  // Mirror the in-app scale (image width / on-screen width) by assuming an
  // ~800px viewing width — keeps the legend legible on full-resolution
  // exports without dominating the image. The earlier `min(w,h)/200`
  // heuristic produced legend boxes wider than the image itself for high-
  // res photos.
  const scale = Math.max(1, width / 800);
  const padding = 10 * scale;
  const fontSize = 10 * scale;
  const lineHeight = fontSize * 1.4;
  const dotRadius = 5 * scale;
  const legendX = padding;
  const legendWidth = 350 * scale;
  const legendHeight = (legend.length + 1) * lineHeight + padding;
  let legendY = height - padding - legend.length * lineHeight - lineHeight;

  ctx.save();
  ctx.fillStyle = "rgba(255, 255, 255, 0.95)";
  ctx.strokeStyle = "rgba(0, 0, 0, 0.1)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  roundedRect(
    ctx,
    legendX - padding / 2,
    legendY - lineHeight,
    legendWidth,
    legendHeight,
    8 * scale,
  );
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = LEGEND_TEXT_COLOR;
  ctx.font = `bold ${fontSize}px sans-serif`;
  ctx.textBaseline = "alphabetic";
  ctx.fillText("Medications", legendX + dotRadius + 8, legendY);
  legendY += lineHeight;

  ctx.font = `${fontSize}px sans-serif`;
  for (const med of legend) {
    ctx.fillStyle = med.color;
    ctx.beginPath();
    ctx.arc(
      legendX + dotRadius,
      legendY - fontSize / 3,
      dotRadius / 2,
      0,
      Math.PI * 2,
    );
    ctx.fill();
    ctx.fillStyle = LEGEND_TEXT_COLOR;
    const dateDisplay = med.date
      ? `${formatDate(med.date, { month: "short", day: "numeric" })}, '${med.date.slice(2, 4)}`
      : "";
    const sitesSuffix = med.count > 1 ? ` (${med.count} sites)` : "";
    const datePart = dateDisplay ? ` - ${dateDisplay}` : "";
    const text = `${med.itemName}: ${med.totalAmount} ${formatUnit(med.unit)}${sitesSuffix}${datePart}`;
    ctx.fillText(text, legendX + dotRadius * 2 + 8, legendY);
    legendY += lineHeight;
  }
  ctx.restore();
}

function drawOutlinedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  outlineUnits: number,
  strokeScale: number,
  fillColor: string,
) {
  ctx.save();
  ctx.strokeStyle = "white";
  ctx.lineWidth = outlineUnits * strokeScale;
  ctx.lineJoin = "round";
  ctx.strokeText(text, x, y);
  ctx.fillStyle = fillColor;
  ctx.fillText(text, x, y);
  ctx.restore();
}
